import { QueryKey } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../httpConstants';
import {
  AssignShopToStaffDto,
  ChangeStaffPasswordDto,
  ChangeStaffRoleDto,
  CreateStaffDto,
  StaffDto,
  UpdateStaffDto,
} from '../types/staff.types';
import {
  useCreateMutation,
  useDetailQuery,
  useListQuery,
  usePaginatedListQuery,
  usePostActionMutation,
  usePutActionMutation,
  useUpdateMutation,
} from './useCrud';

const STAFF_LIST_INVALIDATION: QueryKey[] = [
  [API_ENDPOINTS.TENANT.STAFF.PAGINATED],
  [API_ENDPOINTS.TENANT.STAFF.BASE],
];

export const useStaffListQuery = (includeInactive = false) =>
  useListQuery<StaffDto>(API_ENDPOINTS.TENANT.STAFF.BASE, { includeInactive });

export const useStaffPaginatedQuery = (
  params: {
    pageNumber?: number;
    pageSize?: number;
    search?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    [key: string]: unknown;
  } = {},
  options: { enabled?: boolean } = {},
) => usePaginatedListQuery<StaffDto>(API_ENDPOINTS.TENANT.STAFF.PAGINATED, params, options);

export const useStaffDetailQuery = (id: string) =>
  useDetailQuery<StaffDto>(API_ENDPOINTS.TENANT.STAFF.WITH_ID, { id });

export const useStaffByShopQuery = (shopId: string) =>
  useListQuery<StaffDto>(API_ENDPOINTS.TENANT.STAFF.BY_SHOP(shopId));

export const useMyStaffPermissionsQuery = () =>
  useListQuery(API_ENDPOINTS.TENANT.STAFF.MY_PERMISSIONS);

export const useCreateStaffMutation = () =>
  useCreateMutation<StaffDto, CreateStaffDto>(
    API_ENDPOINTS.TENANT.STAFF.BASE,
    API_ENDPOINTS.TENANT.STAFF.PAGINATED,
  );

export const useUpdateStaffMutation = () =>
  useUpdateMutation<StaffDto, UpdateStaffDto>(
    API_ENDPOINTS.TENANT.STAFF.BASE,
    API_ENDPOINTS.TENANT.STAFF.PAGINATED,
  );

export const useChangeStaffRoleMutation = () =>
  usePutActionMutation<StaffDto, ChangeStaffRoleDto>(
    API_ENDPOINTS.TENANT.STAFF.CHANGE_ROLE,
    STAFF_LIST_INVALIDATION,
  );

export const useAssignStaffShopMutation = () =>
  usePostActionMutation<StaffDto, AssignShopToStaffDto>(
    API_ENDPOINTS.TENANT.STAFF.ASSIGN_SHOP,
    STAFF_LIST_INVALIDATION,
  );

export const useUnassignStaffShopMutation = () =>
  usePostActionMutation<StaffDto>(API_ENDPOINTS.TENANT.STAFF.UNASSIGN_SHOP, STAFF_LIST_INVALIDATION);

export const useStaffResetPasswordMutation = () =>
  usePostActionMutation<boolean>(API_ENDPOINTS.TENANT.STAFF.RESET_PASSWORD, STAFF_LIST_INVALIDATION);

export const useStaffChangePasswordMutation = () =>
  usePutActionMutation<boolean, ChangeStaffPasswordDto>(
    API_ENDPOINTS.TENANT.STAFF.CHANGE_PASSWORD,
    STAFF_LIST_INVALIDATION,
  );

export const useStaffDeactivateMutation = () =>
  usePostActionMutation<StaffDto>(API_ENDPOINTS.TENANT.STAFF.DEACTIVATE, STAFF_LIST_INVALIDATION);

export const useStaffReactivateMutation = () =>
  usePostActionMutation<StaffDto>(API_ENDPOINTS.TENANT.STAFF.REACTIVATE, STAFF_LIST_INVALIDATION);
